import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UpdateProfile from './UpdateProfile';
import ChangePassword from './ChangePassword';
import Metadata from './Metadata';
import Campaigns from './Campaigns';
import BillingAndUsage from './BillingAndUsage';
import HeaderMainPage from '../../header/HeaderMainPage';
import Integrations from './Integrations';
import AuditLog from './AuditLog';
import Help from './Help';
import Settings from './Settings';
import Members from './Members';
import Webhooks from './Webhooks';
import ComingSoonWrapper from '../../header/ComingSoonWrapper';
import AutopilotPage from './AutopilotPage.js';
import { AccountCard, SectionBadge } from './AccountUI';

const UserIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const ListIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
);

const CreditCardIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
);

const LockIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const PuzzleIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M14 7V4.9c0-.4.2-.8.5-.9l2.8-.8c.4-.1.8.2.9.6l.8 2.8c.1.4-.2.8-.6.9l-2.8.8c-.3.1-.5.4-.5.8V7c0 .5-.4 1-1 1H7c-.5 0-1-.4-1-1V4.9c0-.4.2-.8.5-.9l2.8-.8c.4-.1.8.2.9.6l.8 2.8c.1.4-.2.8-.6.9l-2.8.8c-.3.1-.5.4-.5.8V7c0 .5-.4 1-1 1h-1z" />
    </svg>
);

const FileClockIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M16 22h2a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h2" />
        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        <circle cx="8" cy="16" r="4" />
        <path d="M8 14v4l2 2" />
    </svg>
);

const HelpCircleIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const SettingsIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const UsersIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const DatabaseIcon = ({ className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
);

const roboman_api = process.env.REACT_APP_ROBOMAN_API;
const SECTION_STORAGE_KEY = 'account_active_section';
const ALL_SECTIONS = ['profile', 'password', 'members', 'campaigns', 'metadata', 'integrations', 'webhooks', 'audit', 'billing', 'settings', 'help', 'autopilot'];
const LIVE_SECTIONS = new Set(['profile', 'metadata', 'autopilot', 'campaigns', 'help', 'members', 'integrations', 'password']);

const SOON_TITLES = {
    audit: 'Audit Log',
    settings: 'Workspace Settings',
    billing: 'Billing & Usage',
    webhooks: 'Webhooks',
};

const NavButton = ({ item, isActive, isSoon, onClick }) => (
    <button
        onClick={onClick}
        className={`group flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition ${isActive ? 'bg-blue-600 text-white shadow-[0_12px_30px_rgba(37,99,235,0.24)]' : 'text-slate-600 hover:bg-blue-50 hover:text-blue-700'}`}
    >
        <span className={`flex h-10 w-10 items-center justify-center rounded-2xl border transition ${isActive ? 'border-white/15 bg-white/15 text-white' : 'border-blue-100 bg-white text-slate-500 group-hover:border-blue-200 group-hover:text-blue-700'}`}>
            <item.icon className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{item.label}</span>
        </span>
        {isSoon ? <SectionBadge tone={isActive ? 'blue' : 'amber'}>Soon</SectionBadge> : null}
    </button>
);

const ProfilePage = () => <UpdateProfile />;
const ChangePasswordPage = () => <ChangePassword />;
const CampaignsPage = () => <Campaigns />;
const BillingAndUsagePage = () => <BillingAndUsage />;
const IntegrationPage = () => <Integrations />;
const AuditLogPage = () => <AuditLog />;
const HelpPage = () => <Help />;
const SettingsPage = () => <Settings />;
const MembersPage = () => <Members />;
const WebhooksPage = () => <Webhooks />;
const MetadataPage = () => <Metadata />;

const AccountPage = () => {
    const [activeSection, setActiveSection] = useState('profile');
    const [, setUserData] = useState(null);
    const { section } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const res = await fetch(`${roboman_api}/users/me`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: localStorage.getItem('access_token'),
                        Accept: 'application/json',
                    },
                });
                if (!res.ok) {
                    throw new Error('Failed to fetch user data');
                }
                const data = await res.json();
                setUserData(data);
                localStorage.setItem('user_data', JSON.stringify(data));
            } catch (err) {
                console.error('Error fetching user data:', err);
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        const fromUrl = section;
        const fromStorage = localStorage.getItem(SECTION_STORAGE_KEY);
        const initial = (fromUrl && ALL_SECTIONS.includes(fromUrl))
            ? fromUrl
            : (fromStorage && ALL_SECTIONS.includes(fromStorage))
                ? fromStorage
                : 'profile';

        setActiveSection(initial);

        if (!fromUrl || fromUrl !== initial) {
            navigate(`/account/${initial}`, { replace: true });
        }
    }, [navigate, section]);

    const navItems = useMemo(() => ([
        { id: 'profile', label: 'Profile', icon: UserIcon },
        { id: 'password', label: 'Password', icon: LockIcon },
        { id: 'members', label: 'Members', icon: UsersIcon },
        { id: 'campaigns', label: 'Campaigns', icon: ListIcon },
        { id: 'metadata', label: 'Metadata', icon: DatabaseIcon },
        { id: 'autopilot', label: 'Autopilot', icon: ListIcon },
        { id: 'integrations', label: 'Integrations', icon: PuzzleIcon },
        { id: 'audit', label: 'Audit Log', icon: FileClockIcon },
        { id: 'billing', label: 'Billing', icon: CreditCardIcon },
        { id: 'settings', label: 'Settings', icon: SettingsIcon },
        { id: 'help', label: 'Help', icon: HelpCircleIcon },
    ]), []);

    const soon = (key, child) => (
        <ComingSoonWrapper
            title={SOON_TITLES[key] || 'Coming Soon'}
            subtitle="This area is being refined and will be available here soon."
        >
            {child}
        </ComingSoonWrapper>
    );

    const renderSection = () => {
        switch (activeSection) {
            case 'profile': return <ProfilePage />;
            case 'metadata': return <MetadataPage />;
            case 'password': return <ChangePasswordPage />;
            case 'campaigns': return <CampaignsPage />;
            case 'integrations': return <IntegrationPage />;
            case 'audit': return soon('audit', <AuditLogPage />);
            case 'help': return <HelpPage />;
            case 'settings': return soon('settings', <SettingsPage />);
            case 'billing': return soon('billing', <BillingAndUsagePage />);
            case 'members': return <MembersPage />;
            case 'webhooks': return soon('webhooks', <WebhooksPage />);
            default: return <ProfilePage />;
        }
    };

    return (
        <div className="min-h-screen bg-[#f0f2f5] text-slate-900">
            <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
                <div className="absolute left-[-12rem] top-[-8rem] h-[24rem] w-[24rem] rounded-full bg-blue-100/80 blur-3xl" />
                <div className="absolute right-[-10rem] top-[8rem] h-[22rem] w-[22rem] rounded-full bg-sky-100/80 blur-3xl" />
                <div className="absolute bottom-[-8rem] left-[18%] h-[18rem] w-[18rem] rounded-full bg-blue-100/60 blur-3xl" />
            </div>

            <HeaderMainPage />

            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
                    <aside className="xl:sticky xl:top-24 xl:self-start">
                        <AccountCard className="border-blue-100 bg-white/96 p-4">
                            <div className="mb-4 px-2">
                                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-500">Navigation</p>
                            </div>
                            <nav className="space-y-1.5">
                                {navItems.map((item) => {
                                    const isActive = activeSection === item.id;
                                    const isSoon = !LIVE_SECTIONS.has(item.id);
                                    return (
                                        <NavButton
                                            key={item.id}
                                            item={item}
                                            isActive={isActive}
                                            isSoon={isSoon}
                                            onClick={() => {
                                                setActiveSection(item.id);
                                                localStorage.setItem(SECTION_STORAGE_KEY, item.id);
                                                navigate(`/account/${item.id}`);
                                            }}
                                        />
                                    );
                                })}
                            </nav>
                        </AccountCard>
                    </aside>

                    <main>
                        {activeSection === 'autopilot' ? (
                            <div className="overflow-y-auto rounded-[32px] border border-blue-100 bg-white/95 shadow-[0_20px_60px_rgba(59,130,246,0.10)] max-h-[calc(100vh-8rem)]">
                                <AutopilotPage embedded={true} />
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-[32px] border border-blue-100 bg-white/96 shadow-[0_20px_60px_rgba(59,130,246,0.10)]">
                                {renderSection()}
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default AccountPage;

